import dayjs from "dayjs";
import {
  formatDate,
  parseDataBlocks,
} from "../../../utils/helpers";

const getEmbeddableVideoUrl = (value) => {
  const videoUrl = String(value || "").trim();
  if (!videoUrl) return "";

  try {
    const parsedUrl = new URL(videoUrl);
    const hostname = parsedUrl.hostname.replace(/^www\./, "");
    let videoId = "";

    if (hostname === "youtu.be") {
      videoId = parsedUrl.pathname.split("/").filter(Boolean)[0] || "";
    } else if (
      hostname === "youtube.com" ||
      hostname === "m.youtube.com" ||
      hostname === "youtube-nocookie.com"
    ) {
      const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

      if (["embed", "shorts", "live"].includes(pathParts[0])) {
        videoId = pathParts[1] || "";
      } else {
        videoId = parsedUrl.searchParams.get("v") || "";
      }
    }

    if (videoId && /^[a-zA-Z0-9_-]+$/.test(videoId)) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`;
    }
  } catch {
    return videoUrl;
  }

  return videoUrl;
};

const getWeeksBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const weeks = dayjs(endDate).diff(dayjs(startDate), "week");
  return Number.isFinite(weeks) && weeks > 0 ? weeks : null;
};

const getDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "Date to be announced";
  return `${formatDate(startDate, 2)} - ${formatDate(endDate, 2)}`;
};

const getInstructorName = (author) => {
  const fullName = [author?.firstName, author?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || author?.username || "Utrains Instructor";
};

const getPaymentPlans = (data) => {
  const parsedPlans = parseDataBlocks(data?.paymentDescription).filter(
    (plan) => Object.keys(plan).length > 0
  );

  if (parsedPlans.length > 0) {
    return parsedPlans.map((plan, index) => ({
      id: `${plan.name || "plan"}-${index}`,
      label: plan.name || (index === 0 ? "One-time payment" : "Installment"),
      sessionName: plan.sessionName,
      price: plan.price,
      description: plan.description,
      paymentLink: plan.paymentLink || data?.paymentLink,
    }));
  }

  return [
    {
      id: "single-payment",
      label: "One-time payment",
      price: data?.price,
      paymentLink: data?.paymentLink,
    },
  ];
};

const getPlanDescriptionItems = (description) => {
  if (!description) return [];

  return String(description)
    .split(";;")
    .map((item) => item.trim())
    .filter(Boolean);
};

const stripHtmlText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasHtmlContent = (value) => Boolean(stripHtmlText(value));

const escapeRegExp = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceLearningItemPlaceholders = (html, values) => {
  if (!html) return "";

  return Object.entries(values).reduce(
    (content, [key, value]) =>
      content.replace(
        new RegExp(`{{\\s*${escapeRegExp(key)}\\s*}}`, "g"),
        value || ""
      ),
    html
  );
};

const extractRichHeroParts = (html) => {
  if (!html || typeof document === "undefined") {
    return { mainHtml: html || "", benefitsHtml: "" };
  }

  const root = document.createElement("div");
  root.innerHTML = html;

  const benefitsNode = root.querySelector(".ns-benefits-wrap");
  const benefitsHtml = benefitsNode ? benefitsNode.outerHTML : "";
  if (benefitsNode) benefitsNode.remove();

  const heroCopyNode = root.querySelector(".ns-hero-copy");
  if (heroCopyNode) {
    return { mainHtml: heroCopyNode.innerHTML, benefitsHtml };
  }

  const heroContentNode = root.querySelector(".ns-hero-content");
  if (heroContentNode) {
    return { mainHtml: heroContentNode.innerHTML, benefitsHtml };
  }

  const heroSectionNode = root.querySelector(".ns-course-hero");
  if (heroSectionNode) {
    return { mainHtml: heroSectionNode.innerHTML, benefitsHtml };
  }

  return { mainHtml: root.innerHTML, benefitsHtml };
};

export {
  extractRichHeroParts,
  getDateRange,
  getEmbeddableVideoUrl,
  getInstructorName,
  getPaymentPlans,
  getPlanDescriptionItems,
  getWeeksBetween,
  hasHtmlContent,
  replaceLearningItemPlaceholders,
};
